import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { advertisersApi, queryKeys } from '../../lib/api/resources';
import type { AdvertiserRequest } from '../../lib/api/types';
import { useToast } from '../../app/toast';
import { Button, Card, ErrorState, Field, Input, Skeleton } from '../../components/ui';

/** Create or edit an advertiser. Status changes happen from the list, where the consequences are shown. */
export function AdvertiserFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const existing = useQuery({
    queryKey: queryKeys.advertiser(id ?? ''),
    queryFn: () => advertisersApi.get(id!),
    enabled: editing,
  });

  const [form, setForm] = useState<AdvertiserRequest>({
    name: '',
    industry: '',
    contactEmail: '',
    contactPhone: '',
  });

  useEffect(() => {
    if (existing.data) {
      setForm({
        name: existing.data.name,
        industry: existing.data.industry ?? '',
        contactEmail: existing.data.contactEmail ?? '',
        contactPhone: existing.data.contactPhone ?? '',
      });
    }
  }, [existing.data]);

  const save = useMutation({
    mutationFn: (body: AdvertiserRequest) =>
      editing ? advertisersApi.update(id!, body) : advertisersApi.create(body),
    onSuccess: () => {
      toast.success(editing ? 'Anunciante actualizado' : 'Anunciante creado');
      void queryClient.invalidateQueries({ queryKey: ['advertisers'] });
      navigate('/advertisers');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (editing && existing.isLoading) {
    return <Skeleton className="h-64" />;
  }

  if (editing && existing.isError) {
    return <ErrorState message={(existing.error as Error).message} />;
  }

  const update = <K extends keyof AdvertiserRequest>(key: K, value: AdvertiserRequest[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <form
      className="flex max-w-2xl flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate(form);
      }}
    >
      <h1 className="text-headline-lg text-text-primary">
        {editing ? 'Editar anunciante' : 'Nuevo anunciante'}
      </h1>

      <Card>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nombre" htmlFor="name">
            <Input
              id="name"
              required
              maxLength={200}
              value={form.name}
              onChange={(event) => update('name', event.target.value)}
            />
          </Field>

          <Field label="Sector" htmlFor="industry">
            <Input
              id="industry"
              maxLength={100}
              value={form.industry ?? ''}
              onChange={(event) => update('industry', event.target.value)}
            />
          </Field>

          <Field
            label="Correo de contacto"
            htmlFor="contactEmail"
            hint="Debe ser único entre anunciantes"
          >
            <Input
              id="contactEmail"
              type="email"
              maxLength={320}
              value={form.contactEmail ?? ''}
              onChange={(event) => update('contactEmail', event.target.value)}
            />
          </Field>

          <Field label="Teléfono" htmlFor="contactPhone">
            <Input
              id="contactPhone"
              maxLength={40}
              placeholder="+1 809 555 0101"
              value={form.contactPhone ?? ''}
              onChange={(event) => update('contactPhone', event.target.value)}
            />
          </Field>
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <Button type="submit" variant="primary" loading={save.isPending}>
          {editing ? 'Guardar cambios' : 'Crear anunciante'}
        </Button>
        <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
